import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  newDraftId,
  toDraft,
  toPartsContent,
} from "@/lib/assetContent";
import {
  MODEL_FIELD_DESCRIPTION,
  PROFILE_REPLACES_SUMMARY,
  emptyPronunciation,
  languageLabel,
  speechLanguages,
  speechProfileParts,
  type SpeechConfigDraft,
} from "@/lib/speechContent";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState } from "@/ui/states";

import { NamedReadFailureState } from "./NamedReadFailure";

/**
 * Create a speech profile — from scratch, or as a copy of one (#870).
 *
 * ## WHY THE GENERIC DIALOG CANNOT SERVE THIS FAMILY
 *
 * `NewAssetDialog` authors content through `PartsEditor`: a free-text "Part key"
 * input — the manual-key escape hatch the 2026-08-25 canonical amendment
 * forbids — over a raw-JSON textarea. Creating a speech profile there means
 * typing `he/tts_config` into a text box and then hand-writing two typed
 * documents, which is the arbitrary file editor the screen contract rules out
 * and the reason #869 shipped this family with no create destination at all.
 *
 * ## WHY THE FROM-SCRATCH ARM ASKS FOR REAL VALUES INSTEAD OF PLACEHOLDERS
 *
 * The canonical creation journey asks for a name and leaves the content to the
 * editor, and `namedParts.UNWRITTEN_PROMPT_TEXT` is how the other families
 * satisfy a required document without asserting anything clinical. That answer
 * does not transfer here, and the reason is the whole point of the family: a
 * profile that authors both documents makes the pinned engine DECLINE the
 * deployment target's speech parents for that language. A placeholder voice or
 * language code would therefore not be a visibly-unfinished document waiting to
 * be replaced — it would be a complete-looking profile that, if adopted,
 * silences or breaks synthesis for every conversation in the project, with
 * nothing underneath to fall back to. The model is offered but OPTIONAL: the
 * engine stopped requiring it (liv-brain#476) and the tenant deployment, not
 * this field, picks the model (`MODEL_FIELD_DESCRIPTION`).
 *
 * So the dialog asks for the values the service requires, and nothing else: the
 * three required synthesis settings, the instructions, the two gender clauses and the
 * speaker anchor. The word tables open EMPTY, which is a complete authored
 * statement rather than a gap — the engine reads an empty table as "this profile
 * substitutes nothing here" — and are filled in the profile's own editor
 * afterwards, which is the post-create step the canonical journey describes.
 *
 * ## A COPY IS VERBATIM, unlike a Rule's
 *
 * `NewRulesDialog` deliberately drops the documents that DEEP-MERGE onto base
 * canon, because copying them would write hundreds of entries restating what
 * base already says. Both speech roles carry the same `structured_deep_merge`
 * strategy in the placement registry — but BASE CANON SHIPS NO SPEECH FILES AT
 * ALL (verified against the seeded catalog: the family has no seeded asset),
 * so there is nothing underneath for a copy to restate and the redundancy that
 * rule exists to prevent cannot arise. Dropping them would be the real defect:
 * a profile missing either document is exactly the incomplete pair the service
 * and the engine both refuse. So a copy carries every part exactly.
 */

const NAME_MAX_LENGTH = 60;

/**
 * The languages a new profile can be written at, and why the control is a list.
 *
 * Speech resolves per language and the source packs author `he` only, so the
 * family declares no required language — `speech_profile._require_language` only
 * insists that a part NAMES one. A free-text field here would be the part-key
 * escape hatch by another name (the prefix IS the key's first half), so the
 * control offers the languages this product carries and the clinician picks one.
 * `rulesContent`'s own product names, through `languageLabel`.
 */
const CREATABLE_LANGUAGES = ["he", "en"] as const;

/** What a refusal computed in the BROWSER says, so nothing is ever swallowed. */
const REFUSED_CONTENT =
  "This profile's documents could not be prepared, so nothing was created.";

export function NewSpeechProfileDialog({
  copyFrom,
  onClose,
}: {
  /** The profile being copied, or absent for a from-scratch profile. */
  copyFrom?: LibraryAsset;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const nameErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [language, setLanguage] = useState<string>("he");
  const [draft, setDraft] = useState<SpeechConfigDraft>({
    model: "",
    voice: "",
    languageCode: "",
    temperature: "",
    instructions: "",
    female: "",
    male: "",
  });
  const [anchor, setAnchor] = useState("");
  const [blocked, setBlocked] = useState<string | null>(null);

  // `null` for the version rather than an empty string: that is the hook's own
  // disable signal, so the from-scratch arm never issues a read at all.
  const source = useAssetVersionQuery(
    copyFrom?.id ?? "",
    copyFrom?.latestVersion ?? null,
  );
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom?.id ?? "", copyFrom?.latestVersion ?? ""),
  );

  const stored =
    copyFrom !== undefined && source.isSuccess && isPartsContent(source.data.content)
      ? source.data.content
      : null;
  const copiedLanguages = stored === null ? [] : speechLanguages(toDraft(stored));

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  const unread = copyFrom !== undefined && (source.isPaused || source.isError);
  const scratchReady =
    draft.voice.trim() !== "" &&
    draft.languageCode.trim() !== "" &&
    draft.temperature.trim() !== "" &&
    draft.instructions.trim() !== "" &&
    draft.female.trim() !== "" &&
    draft.male.trim() !== "" &&
    anchor.trim() !== "";
  const ready =
    name.trim() !== "" && (copyFrom !== undefined ? stored !== null : scratchReady);

  function submit() {
    setBlocked(null);
    const parts =
      copyFrom !== undefined
        ? stored === null
          ? null
          : toDraft(stored)
        : speechProfileParts(
            language,
            draft,
            emptyPronunciation(anchor),
            newDraftId,
          );
    if (parts === null) {
      setBlocked(
        `The Editor Service returned ${copyFrom?.name ?? "this profile"} in a form this editor does not recognise, so nothing was created.`,
      );
      return;
    }
    const content = toPartsContent(parts);
    if (!content.ok) {
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }
    create.mutate(
      {
        assetType: "speech_profile",
        name: name.trim(),
        content: content.content,
        note:
          copyFrom !== undefined
            ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`
            : null,
        // NO `placementCoordinate`. A speech profile is a fixed-placement
        // family whose two files land at `conversation_agent/{lang}/…`, and the
        // service refuses a client-chosen coordinate on this type.
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(`/library/rules/${created.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : "New speech profile"}
        description={
          copyFrom
            ? "Creates your own speech profile starting from this one, in every language it carries. The original stays exactly as it is."
            : PROFILE_REPLACES_SUMMARY
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button disabled={busy || !ready} onClick={submit}>
              {copyFrom ? "Create copy" : "Create profile"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description="Short name for this speech profile."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          {copyFrom !== undefined ? (
            <>
              {source.isPending && !unread ? (
                <LoadingState
                  label={`Loading content from ${copyFrom.name}`}
                  rows={2}
                />
              ) : null}

              {unread ? (
                <NamedReadFailureState
                  failure={source.isPaused ? source.failureReason : source.error}
                  isPaused={source.isPaused}
                  retry={retrySource}
                  subject="Asset content"
                  title="This speech profile's content could not be read"
                  description={`The Editor Service did not return ${copyFrom.name} at ${copyFrom.latestVersion}, so there is nothing to copy from yet.`}
                  deniedDescription="You are not authorized to read this speech profile's content, so it cannot be copied."
                  offlineDescription={`${copyFrom.name} at ${copyFrom.latestVersion} could not be reached, so there is nothing to copy from yet. It loads on its own when the connection returns.`}
                />
              ) : null}

              {copiedLanguages.length > 0 ? (
                // WHAT THE COPY WILL CARRY, in the editor's own words — the
                // shape, never the keys. Verbatim, because neither speech
                // document merges onto anything.
                <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                  <p>The copy carries, exactly as the original holds them:</p>
                  <ul className="mt-1 list-inside list-disc">
                    {copiedLanguages.map((view) => (
                      <li key={view.language}>
                        {view.label} — {view.config === null ? "no" : "the"}{" "}
                        synthesis settings,{" "}
                        {view.pronunciation === null ? "no" : "the"} pronunciation
                        tables
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <Field
                label="Language"
                // NO PROMISE OF A LATER ACTION. This read "…a profile can
                // carry more than one — the others are added on the profile's
                // own page", and no such control exists: the editor renders the
                // languages an asset already carries and authors no part key,
                // deliberately. A second language is added through the API, so
                // the description says what this dialog does and stops there.
                description="The language this profile is written at. Its synthesis settings and pronunciation tables are written under this language."
              >
                {({ inputId, describedBy }) => (
                  <Select
                    id={inputId}
                    aria-describedby={describedBy}
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                  >
                    {CREATABLE_LANGUAGES.map((tag) => (
                      <option key={tag} value={tag}>
                        {languageLabel(tag)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Model" description={MODEL_FIELD_DESCRIPTION}>
                  {({ inputId, describedBy }) => (
                    <Input
                      id={inputId}
                      dir="ltr"
                      aria-describedby={describedBy}
                      value={draft.model}
                      onChange={(event) =>
                        setDraft({ ...draft, model: event.target.value })
                      }
                    />
                  )}
                </Field>
                <Field label="Voice" description="The voice name the model synthesizes with.">
                  {({ inputId, describedBy }) => (
                    <Input
                      id={inputId}
                      dir="ltr"
                      aria-describedby={describedBy}
                      value={draft.voice}
                      onChange={(event) =>
                        setDraft({ ...draft, voice: event.target.value })
                      }
                    />
                  )}
                </Field>
                <Field
                  label="Language code"
                  description="The spoken language and region, as the model names it — for example he-IL."
                >
                  {({ inputId, describedBy }) => (
                    <Input
                      id={inputId}
                      dir="ltr"
                      aria-describedby={describedBy}
                      value={draft.languageCode}
                      onChange={(event) =>
                        setDraft({ ...draft, languageCode: event.target.value })
                      }
                    />
                  )}
                </Field>
                <Field
                  label="Temperature"
                  description="How much the voice varies between renderings. Write a whole number without a decimal point: 0 means no variation."
                >
                  {({ inputId, describedBy }) => (
                    <Input
                      id={inputId}
                      dir="ltr"
                      inputMode="decimal"
                      aria-describedby={describedBy}
                      value={draft.temperature}
                      onChange={(event) =>
                        setDraft({ ...draft, temperature: event.target.value })
                      }
                    />
                  )}
                </Field>
              </div>

              <Field
                label="Speaking instructions"
                description="Sent to the model with every turn, exactly as written here."
              >
                {({ inputId, describedBy }) => (
                  <Textarea
                    id={inputId}
                    dir="auto"
                    rows={3}
                    aria-describedby={describedBy}
                    value={draft.instructions}
                    onChange={(event) =>
                      setDraft({ ...draft, instructions: event.target.value })
                    }
                  />
                )}
              </Field>

              <Field
                label="Instruction for a female listener"
                description="Added to the speaking instructions when the patient's recorded gender is female."
              >
                {({ inputId, describedBy }) => (
                  <Textarea
                    id={inputId}
                    dir="auto"
                    rows={3}
                    aria-describedby={describedBy}
                    value={draft.female}
                    onChange={(event) =>
                      setDraft({ ...draft, female: event.target.value })
                    }
                  />
                )}
              </Field>

              <Field
                label="Instruction for a male listener"
                description="Added to the speaking instructions when the patient's recorded gender is male."
              >
                {({ inputId, describedBy }) => (
                  <Textarea
                    id={inputId}
                    dir="auto"
                    rows={3}
                    aria-describedby={describedBy}
                    value={draft.male}
                    onChange={(event) =>
                      setDraft({ ...draft, male: event.target.value })
                    }
                  />
                )}
              </Field>

              <Field
                label="Speaker anchor"
                description="The word that has to come immediately before a speaker pronunciation for it to apply. The word tables themselves start empty and are filled in on the profile's own page."
              >
                {({ inputId, describedBy }) => (
                  <Input
                    id={inputId}
                    dir="auto"
                    aria-describedby={describedBy}
                    value={anchor}
                    onChange={(event) => setAnchor(event.target.value)}
                  />
                )}
              </Field>
            </>
          )}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
